import DataSource from 'devextreme/data/data_source'
import { memo, useRef } from 'react'
import type { analisi, contatto_aziendale, entita_aziendale } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { AzureUserInfo } from '@/auth/azure/azureManager'
import { GridCellColor } from '@/enums'
import {
  Column,
  DataGrid,
  DataGridRef,
  DataGridTypes,
  Editing,
  Item,
  Paging,
  StateStoring,
  Toolbar,
  Lookup,
  RequiredRule,
  CustomRule,
} from 'devextreme-react/data-grid'
import type { CellPreparedEvent } from 'devextreme/ui/data_grid'
import { useScreenSize } from '@/themes/media-query'
import { ClickEvent } from 'devextreme/ui/button'
import { Roles } from '@/auth/azure/Roles'
import { isUserRoleAllowed } from '@/routes/utils'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { useNavigate } from 'react-router-dom'

const BriefAnalysisGrid = (props: {
  dataSource: DataSource<any, any>
  userInfo: AzureUserInfo | undefined
  storageKey?: string
  isNestedData?: boolean
  analisiList?: analisi[] | undefined
  allowEditing?: boolean
  onDeleting?: (analysisId: number) => void
  onAdding?: (analysisId: number) => void
}) => {
  const {
    dataSource,
    userInfo,
    storageKey,
    isNestedData = false,
    allowEditing = false,
    analisiList,
    onDeleting,
    onAdding,
  } = props

  const currentScreenSize = useScreenSize()
  const gridRef = useRef<DataGridRef>(null)
  const navigate = useNavigate()

  const getPath = (field: string) => (isNestedData ? `analisi.${field}` : field)

  const onCellPrepared = (e: CellPreparedEvent) => {
    if (e.rowType === 'data' && e.column.name === getPath('stato.nome') && e.value) {
      switch (e.value) {
        case 'COMPLETATA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.EMERALD}`

          break
        }
        case 'INVIATA OFFERTA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.SALMON}`

          break
        }
        case 'APERTA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.YELLOW}`

          break
        }
        case 'ANNULLATA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.ORANGE}`
          break
        }
        case 'SUPERATA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.AQUA}`
          break
        }
        case 'ORDINATA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.LILAC}`
          break
        }
      }
    }
  }

  const calculateRiferimentiValue = (rowData: any) => {
    // Access the correct data level
    const data = isNestedData ? rowData.analisi : rowData
    let riferimenti: string = ''
    data?.riferimenti?.map(
      (contatto: contatto_aziendale) => (riferimenti = `${riferimenti + contatto.fullname?.toUpperCase()}; `),
    )
    data?.riferimenti_entita?.map(
      (entita: entita_aziendale) => (riferimenti = `${riferimenti + entita.nome?.toUpperCase()}; `),
    )
    return riferimenti.trim()
  }

  const cellRiferimentiRender = (cellData: DataGridTypes.ColumnCellTemplateData) => {
    const data = isNestedData ? cellData.data.analisi : cellData.data
    return (
      <>
        {data?.riferimenti?.map((contatto: contatto_aziendale, index: number) => {
          return (
            <div className={`dx-tag-content custom-tag-padding`} key={`ctc-${index}`}>
              <span>{contatto.fullname}</span>
            </div>
          )
        })}
      </>
    )
  }

  return (
    <>
      <DataGrid
        id={'brief-analysis-grid'}
        ref={gridRef}
        dataSource={dataSource}
        noDataText="Nessuna analisi associata"
        showBorders={true}
        showColumnLines={true}
        focusedRowEnabled={true}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        allowColumnResizing={true}
        rowAlternationEnabled={true}
        wordWrapEnabled={false}
        repaintChangesOnly={true}
        width="100%"
        onCellPrepared={onCellPrepared}
        onRowDblClick={(e: DataGridTypes.RowDblClickEvent<any, any>) => {
          if (e.rowType === 'data') {
            const analisiId = isNestedData ? e.data.analisi?.id : e.data.id
            navigate(`/analysis/${analisiId}`)
          }
        }}
        onSaving={async (e: DataGridTypes.SavingEvent<any, any>) => {
          if (allowEditing) {
            try {
              e.cancel = true
              gridRef?.current?.instance().beginCustomLoading('Associazione analisi personalizzazione')
              if (e.changes[0].type === 'insert' && onAdding) await onAdding(e.changes[0].data.ded_Dis) //qui in ded_dis ho l'id
              if (e.changes[0].type === 'remove' && onDeleting) await onDeleting(e.changes[0].key)
              gridRef?.current?.instance().refresh()
            } catch (error) {
              console.log('error', error)
            } finally {
              gridRef?.current?.instance().cancelEditData()
              gridRef?.current?.instance().endCustomLoading()
            }
          }
        }}
      >
        {storageKey && <StateStoring enabled={true} type="localStorage" storageKey={storageKey} savingTimeout={50} />}
        <Paging defaultPageSize={8} />
        {allowEditing && (
          <Editing
            mode={'row'}
            allowUpdating={false}
            allowAdding={onAdding ? true : false}
            allowDeleting={onDeleting ? true : false}
            texts={{
              addRow: 'Aggiungi',
              deleteRow: 'Elimina',
              confirmDeleteMessage: 'Sei sicuro di voler eliminare il record?',
              confirmDeleteTitle: 'Elimina',
              saveRowChanges: 'Salva',
              cancelRowChanges: 'Annulla',
            }}
          />
        )}
        <Column
          dataField={getPath('id')}
          caption={'ID'}
          visible={false}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
          }
          allowEditing={false}
        />
        <Column
          dataField={getPath('ded_Dis')}
          caption={'CODICE'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: analisi = isNestedData ? cellData.row.data.analisi : cellData.row.data
            return (
              <ObjectCodeLink
                url={`/analysis/${data?.id}`}
                text={data?.ded_Dis}
                disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
              />
            )
          }}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined
          }
        >
          <Lookup
            dataSource={analisiList}
            displayExpr={(item: analisi) => {
              return item ? `${item.ded_Dis}${item.oggetto ? ' - ' : ''}${item.oggetto ?? ''}` : ``
            }}
            valueExpr="id"
            allowClearing={true}
          />
          <RequiredRule message="Il codice analisi è obbligatorio" />
          <CustomRule
            message="Analisi già associata alla personalizzazione"
            validationCallback={(options: any) => {
              const value = options.value
              if (!value) return true

              const rows: analisi[] | undefined = gridRef.current?.instance().getDataSource().items()
              const exists = rows?.some((row: analisi) => row.id === value)

              return !exists
            }}
          />
        </Column>
        <Column
          dataField={getPath('revisione')}
          caption={'REV'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 2 : undefined
          }
          allowEditing={false}
        />
        <Column
          name={'info_riferimenti'}
          caption={'RIFERIMENTI'}
          calculateCellValue={calculateRiferimentiValue}
          cellRender={cellRiferimentiRender}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
          }
          allowEditing={false}
        />
        <Column
          dataField={getPath('creatore.fullname')}
          caption={'AUTORE'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined
          }
          allowEditing={false}
        />
        <Column
          dataField={getPath('oggetto')}
          caption={'OGGETTO'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 8 : undefined
          }
          allowEditing={false}
        />
        <Column
          dataField={getPath('stato.nome')}
          caption={'STATO'}
          hidingPriority={
            currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
          }
          allowEditing={false}
        />
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                gridRef.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item name="addRowButton"></Item>
        </Toolbar>
      </DataGrid>
    </>
  )
}

const BriefAnalysisGridMemoed = memo(BriefAnalysisGrid, (oldProps, newProps) => {
  return oldProps.dataSource === newProps.dataSource
})

export default BriefAnalysisGridMemoed
